import { useEffect, useState } from "react";
import { api, euro } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Coffee, ForkKnife, BeerStein, Coins, Plus, Trash, Gear } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Extras-Verwaltung pro Buchung: Frühstück, Abendessen, Getränke, Trinkgeld, Sonstiges.
 * - Frühstück / Abendessen: Menge × Standardpreis (aus Settings, überschreibbar)
 * - Getränke / Trinkgeld / Sonstiges: freier Betrag
 * - Trinkgeld erscheint nie auf Rechnung, nur in der Statistik.
 */
export default function BookingExtrasDialog({ open, booking, onClose, onChanged }) {
  const [extras, setExtras] = useState([]);
  const [totals, setTotals] = useState({ extras_total: 0, extras_invoiceable_total: 0, extras_tip_total: 0 });
  const [prices, setPrices] = useState({ breakfast: 19.99, dinner: 19.99 });
  const [savingPrices, setSavingPrices] = useState(false);
  const [showPriceEdit, setShowPriceEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notifs, setNotifs] = useState({
    notify_confirmation: true, notify_checkin: true, notify_meals: true, notify_review: true, notify_whatsapp: true
  });
  const [notifsBusy, setNotifsBusy] = useState(false);

  const load = async () => {
    if (!booking?.id) return;
    try {
      const [{ data: ex }, { data: pr }, { data: b }] = await Promise.all([
        api.get(`/bookings/${booking.id}/extras`),
        api.get(`/settings/extras-prices`),
        api.get(`/bookings/${booking.id}`),
      ]);
      setExtras(ex.extras || []);
      setTotals({
        extras_total: ex.extras_total || 0,
        extras_invoiceable_total: ex.extras_invoiceable_total || 0,
        extras_tip_total: ex.extras_tip_total || 0,
      });
      setPrices({
        breakfast: pr.breakfast ?? 19.99,
        dinner:    pr.dinner    ?? 19.99,
      });
      setNotifs({
        notify_confirmation: b?.notify_confirmation !== false,
        notify_checkin:      b?.notify_checkin !== false,
        notify_meals:        b?.notify_meals !== false,
        notify_review:       b?.notify_review !== false,
        notify_whatsapp:     b?.notify_whatsapp !== false,
      });
    } catch (_) {}
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, booking?.id]);

  const addExtra = async (payload) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/extras`, payload);
      await load();
      onChanged?.();
      toast.success(`${payload.label || "Extra"} hinzugefügt`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Extra nicht speichern");
    } finally { setBusy(false); }
  };

  const removeExtra = async (id) => {
    if (busy) return;
    if (!window.confirm("Diese Position wirklich löschen?")) return;
    setBusy(true);
    try {
      await api.delete(`/bookings/${booking.id}/extras/${id}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Löschen");
    } finally { setBusy(false); }
  };

  const savePrices = async () => {
    const norm = (v) => {
      const n = parseFloat(String(v ?? "").replace(",", "."));
      return isNaN(n) ? 0 : n;
    };
    setSavingPrices(true);
    try {
      const { data } = await api.put(`/settings/extras-prices`, {
        breakfast: norm(prices.breakfast),
        dinner:    norm(prices.dinner),
      });
      setPrices(data);
      setShowPriceEdit(false);
      toast.success(`Standardpreise gespeichert · 🥐 ${data.breakfast.toFixed(2).replace(".",",")}€ · 🍽️ ${data.dinner.toFixed(2).replace(".",",")}€`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Preise nicht speichern");
    } finally { setSavingPrices(false); }
  };

  const updateNotif = async (key, value) => {
    setNotifs(n => ({ ...n, [key]: value }));
    if (notifsBusy) return;
    setNotifsBusy(true);
    try {
      await api.patch(`/bookings/${booking.id}/notifications`, { [key]: value });
      toast.success(value ? "Nachricht aktiviert" : "Nachricht deaktiviert");
      onChanged?.();
    } catch (e) {
      setNotifs(n => ({ ...n, [key]: !value })); // rollback
      toast.error("Konnte nicht speichern");
    } finally { setNotifsBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="booking-extras-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Extras zur Buchung</span>
            <span className="text-xs font-normal text-muted-foreground">
              {booking?.guest?.first_name} {booking?.guest?.last_name} · {booking?.room?.name}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Auto-Nachrichten Toggle-Bar */}
        <div className="rounded-lg border bg-slate-50 p-2 space-y-1">
          <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground px-1">📧 Auto-Mails an Gast</div>
          <div className="grid grid-cols-2 gap-1">
            <NotifyToggle checked={notifs.notify_confirmation} onChange={v => updateNotif("notify_confirmation", v)} label="Bestätigung" testid="nx-confirm"/>
            <NotifyToggle checked={notifs.notify_checkin}      onChange={v => updateNotif("notify_checkin", v)}      label="Check-in"    testid="nx-checkin"/>
            <NotifyToggle checked={notifs.notify_meals}        onChange={v => updateNotif("notify_meals", v)}        label="Mahlzeiten"  testid="nx-meals"/>
            <NotifyToggle checked={notifs.notify_review}       onChange={v => updateNotif("notify_review", v)}       label="Bewertung"   testid="nx-review"/>
          </div>
        </div>

        {/* Schnell-Kacheln */}
        <div className="grid grid-cols-2 gap-3">
          <QuickTile
            testid="extra-breakfast"
            icon={<Coffee size={22} weight="duotone" className="text-amber-600" />}
            label="Frühstück"
            hint={`${euro(prices.breakfast)} / Person`}
            onAdd={(qty) => addExtra({ kind: "breakfast", qty, unit_price: prices.breakfast, label: "Frühstück" })}
            qtyMode
          />
          <QuickTile
            testid="extra-dinner"
            icon={<ForkKnife size={22} weight="duotone" className="text-red-600" />}
            label="Abendessen"
            hint={`${euro(prices.dinner)} / Person`}
            onAdd={(qty) => addExtra({ kind: "dinner", qty, unit_price: prices.dinner, label: "Abendessen" })}
            qtyMode
          />
          <QuickTile
            testid="extra-drinks"
            icon={<BeerStein size={22} weight="duotone" className="text-violet-600" />}
            label="Getränke"
            hint="Betrag frei"
            amountMode
            onAdd={(amount) => addExtra({ kind: "drinks", qty: 1, unit_price: amount, label: "Getränke" })}
          />
          <QuickTile
            testid="extra-tip"
            icon={<Coins size={22} weight="duotone" className="text-emerald-600" />}
            label="Trinkgeld"
            hint="nur Statistik"
            amountMode
            onAdd={(amount) => addExtra({ kind: "tip", qty: 1, unit_price: amount, label: "Trinkgeld" })}
          />
        </div>

        <OtherTile onAdd={(label, amount) => addExtra({ kind: "other", qty: 1, unit_price: amount, label })} />

        {/* Preise-Toggle */}
        <div className="flex justify-end">
          <button className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
                  onClick={() => setShowPriceEdit(v => !v)}
                  data-testid="extras-edit-prices">
            <Gear size={12}/> Standardpreise anpassen
          </button>
        </div>
        {showPriceEdit && (
          <div className="p-3 rounded-lg border border-dashed grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Frühstück (€/Person)</Label>
              <Input type="number" step="0.5" value={prices.breakfast}
                     onChange={e => setPrices(p => ({ ...p, breakfast: e.target.value }))}
                     data-testid="extras-price-breakfast" />
            </div>
            <div>
              <Label className="text-xs">Abendessen (€/Person)</Label>
              <Input type="number" step="0.5" value={prices.dinner}
                     onChange={e => setPrices(p => ({ ...p, dinner: e.target.value }))}
                     data-testid="extras-price-dinner" />
            </div>
            <div className="col-span-2 flex justify-end">
              <Button onClick={savePrices} disabled={savingPrices} size="sm" data-testid="extras-save-prices">
                Speichern
              </Button>
            </div>
          </div>
        )}

        {/* Liste */}
        <div className="mt-2">
          <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Erfasste Extras</div>
          {extras.length === 0 ? (
            <div className="text-sm text-muted-foreground italic py-3 text-center">Noch keine Extras erfasst.</div>
          ) : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {extras.map(e => (
                <div key={e.id} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {e.label}
                      {e.kind === "tip" && <span className="ml-2 text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">nur Statistik</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {e.qty > 1 ? `${e.qty} × ${euro(e.unit_price)} = ` : ""}<span className="font-semibold text-foreground">{euro(e.total)}</span>
                    </div>
                  </div>
                  <button onClick={() => removeExtra(e.id)} className="text-red-600 hover:bg-red-50 p-1 rounded"
                          title="Löschen" data-testid={`extra-del-${e.id}`}>
                    <Trash size={14} weight="bold"/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summen */}
        <div className="mt-2 pt-3 border-t space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Auf Rechnung</span><span className="font-semibold">{euro(totals.extras_invoiceable_total)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Trinkgeld (nur Statistik)</span><span className="font-semibold text-emerald-700">{euro(totals.extras_tip_total)}</span></div>
          <div className="flex justify-between text-base pt-1 border-t"><span className="font-bold">Extras gesamt</span><span className="font-bold">{euro(totals.extras_total)}</span></div>
        </div>

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose} data-testid="extras-close">Schließen</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QuickTile({ icon, label, hint, onAdd, qtyMode, amountMode, testid }) {
  const [val, setVal] = useState(qtyMode ? 1 : "");
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (qtyMode) {
      const q = parseFloat(val) || 0;
      if (q <= 0) return;
      setBusy(true);
      await onAdd(q);
      setVal(1);
      setBusy(false);
    } else if (amountMode) {
      const a = parseFloat(String(val).replace(",", ".")) || 0;
      if (a <= 0) return;
      setBusy(true);
      await onAdd(a);
      setVal("");
      setBusy(false);
    }
  };
  return (
    <div className="p-3 rounded-xl border bg-white flex flex-col gap-2" data-testid={testid}>
      <div className="flex items-center gap-2">
        {icon}
        <div className="flex-1">
          <div className="text-sm font-bold">{label}</div>
          <div className="text-[11px] text-muted-foreground">{hint}</div>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <Input
          type={qtyMode ? "number" : "text"}
          inputMode={qtyMode ? "numeric" : "decimal"}
          value={val}
          min={qtyMode ? 1 : undefined}
          step={qtyMode ? 1 : undefined}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handle()}
          placeholder={qtyMode ? "Anzahl" : "z.B. 12.50"}
          className="h-8 text-sm"
          data-testid={`${testid}-input`}
        />
        <Button size="sm" onClick={handle} disabled={busy}
                className="h-8 px-2 bg-primary" data-testid={`${testid}-add`}>
          <Plus size={14} weight="bold"/>
        </Button>
      </div>
    </div>
  );
}

function NotifyToggle({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer hover:bg-slate-100" data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="rounded"/>
      <span className="text-xs font-medium">{label}</span>
    </label>
  );
}


function OtherTile({ onAdd }) {
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    const a = parseFloat(String(amount).replace(",", ".")) || 0;
    const lbl = label.trim() || "Sonstiges";
    if (a <= 0) return;
    setBusy(true);
    await onAdd(lbl, a);
    setLabel(""); setAmount("");
    setBusy(false);
  };
  return (
    <div className="p-3 rounded-xl border border-dashed bg-slate-50">
      <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Sonstiges (frei)</div>
      <div className="flex items-center gap-1.5">
        <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="Bezeichnung" className="h-8 text-sm flex-1" data-testid="extra-other-label"/>
        <Input value={amount} onChange={e => setAmount(e.target.value)} placeholder="Betrag" className="h-8 text-sm w-20" data-testid="extra-other-amount"
               onKeyDown={e => e.key === "Enter" && handle()}/>
        <Button size="sm" onClick={handle} disabled={busy} className="h-8 px-2 bg-primary" data-testid="extra-other-add">
          <Plus size={14} weight="bold"/>
        </Button>
      </div>
    </div>
  );
}
